import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Plus, Send } from 'lucide-react';
import { SectionCard, Badge } from './GDLComponents';
import DemoDirector from './DemoDirector';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ngn = n => `\u20A6${Math.round(n || 0).toLocaleString()}`;

export default function GDLAdminPanels() {
  const [tab, setTab] = useState('director');
  return (
    <div style={{ marginTop: 24 }} data-testid="admin-extended-panels">
      {/* Tab strip */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          ['director', 'Demo Director'],
          ['intelligence', 'Atom intelligence'],
          ['revenue', 'Revenue summary'],
          ['merchants', 'GDL+ Checkout merchants'],
          ['calculator', 'Salary calculator'],
          ['gdlpay', 'GDL Pay payroll'],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            data-testid={`admin-tab-${id}`}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid ' + (tab === id ? (id === 'director' ? '#0A1F44' : '#B03140') : '#E5E7EB'),
              background: tab === id ? (id === 'director' ? '#0A1F44' : '#B03140') : '#fff',
              color: tab === id ? '#fff' : '#374151',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'director' && <DemoDirector />}
      {tab === 'intelligence' && <IntelligencePanel />}
      {tab === 'revenue' && <RevenuePanel />}
      {tab === 'merchants' && <MerchantsPanel />}
      {tab === 'calculator' && <CalculatorPanel />}
      {tab === 'gdlpay' && <GdlPayPanel />}
    </div>
  );
}

// ─── Section H ───────────────────────────────────────────────
function IntelligencePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/admin/atom-intelligence`).then(r => setData(r.data));
  }, []);
  if (!data) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  const max = Math.max(...data.compounding_curve.map(p => p.capability_score), 1);
  return (
    <div data-testid="intelligence-panel" style={{ display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
      {[
        ['Suggestions made', data.stats.suggestions_made, '#111827'],
        ['Accepted', data.stats.accepted, '#10B981'],
        ['Dismissed', data.stats.dismissed, '#9CA3AF'],
        ['Chats handled', data.stats.chats_handled, '#B03140'],
        ['Support queries deflected', data.stats.deflected_support_queries, '#7C3AED'],
      ].map(([l, v, c]) => (
        <SectionCard key={l}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{l}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c, marginTop: 4 }}>{v}</div>
        </SectionCard>
      ))}
      <SectionCard style={{ gridColumn: '1 / -1' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
          Compounding capability (months 0 → 12)
        </div>
        <svg viewBox="0 0 600 160" style={{ width: '100%', height: 160 }}>
          <polyline fill="none" stroke="#B03140" strokeWidth="2.5"
            points={data.compounding_curve.map((p, i) =>
              `${i * (600 / 12)},${160 - (p.capability_score / max) * 140}`).join(' ')} />
          {data.compounding_curve.map((p, i) => (
            <circle key={i} cx={i * (600 / 12)}
              cy={160 - (p.capability_score / max) * 140}
              r="3" fill="#B03140" />
          ))}
          <line x1="0" y1="160" x2="600" y2="160" stroke="#E5E7EB" />
        </svg>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 12, lineHeight: 1.55 }}>
          {data.narrative}
        </div>
      </SectionCard>
    </div>
  );
}

function RevenuePanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/admin/revenue-summary`).then(r => setData(r.data));
  }, []);
  if (!data) return <div style={{ color: '#6B7280' }}>Loading…</div>;
  return (
    <div data-testid="revenue-panel" style={{ display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
      {data.channels.map(c => (
        <SectionCard key={c.label}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#B03140', marginTop: 4 }}>
            {ngn(c.amount)}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>per month</div>
        </SectionCard>
      ))}
      <SectionCard style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #B03140, #8E2233)', color: '#fff' }}>
        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7,
          textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total monthly run-rate</div>
        <div style={{ fontSize: 30, fontWeight: 700, marginTop: 4 }}>
          {ngn(data.total_monthly_run_rate)}
        </div>
        <div style={{ fontSize: 13, marginTop: 10, opacity: 0.9, lineHeight: 1.5 }}>
          {data.framing}
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Section D ───────────────────────────────────────────────
function MerchantsPanel() {
  const [merchants, setMerchants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ legal_name: '', display_name: '',
    settlement_bank: 'GTBank', settlement_account: '',
    webhook_url: '', brand_color: '#FF8C00' });
  const [newKey, setNewKey] = useState(null);
  const [showOnboard, setShowOnboard] = useState(false);
  const [orderState, setOrderState] = useState({});

  const refresh = () => {
    axios.get(`${API}/api/demo/gdl/admin/merchants`).then(r => setMerchants(r.data));
    axios.get(`${API}/api/demo/gdl/admin/checkout/commission-summary`).then(r => setSummary(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const onboard = async () => {
    if (!form.legal_name || !form.settlement_account) return;
    try {
      const { data } = await axios.post(`${API}/api/demo/gdl/admin/merchants/onboard`, form);
      setNewKey({ api_key: data.api_key, display_name: data.display_name });
      setShowOnboard(false);
      setForm({ legal_name: '', display_name: '', settlement_bank: 'GTBank',
                  settlement_account: '', webhook_url: '', brand_color: '#FF8C00' });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Onboard failed'); }
  };

  const quickOrder = async (m) => {
    try {
      const { data: ord } = await axios.post(`${API}/api/demo/gdl/checkout/orders`, {
        merchant_id: m.id, amount: 25000, customer_name: 'Adaeze Iwu',
        description: 'Birthday cake order',
      });
      setOrderState(s => ({ ...s, [m.id]: { ...ord, settled: false } }));
    } catch (e) { alert('Order create failed'); }
  };

  const simulatePaid = async (m) => {
    const ord = orderState[m.id];
    if (!ord) return;
    try {
      const { data: r } = await axios.post(
        `${API}/api/demo/gdl/checkout/orders/${ord.id}/simulate-paid`);
      setOrderState(s => ({ ...s, [m.id]: { ...ord, ...r, settled: true } }));
      refresh();
    } catch (e) { alert('Simulate paid failed'); }
  };

  return (
    <div data-testid="merchants-panel">
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            ['Merchants', summary.merchant_count, '#111827'],
            ['Orders paid', summary.orders_paid, '#10B981'],
            ['Volume', ngn(summary.total_volume), '#B03140'],
            ['Commission earned', ngn(summary.total_commission), '#7C3AED'],
          ].map(([l, v, c]) => (
            <SectionCard key={l}>
              <div style={{ fontSize: 12, color: '#6B7280' }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c, marginTop: 4 }}>{v}</div>
            </SectionCard>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Partner merchants</div>
        <button onClick={() => setShowOnboard(s => !s)} data-testid="onboard-merchant-toggle"
          style={{ background: '#B03140', color: '#fff', border: 'none', padding: '8px 16px',
            borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16}/> Onboard a merchant
        </button>
      </div>

      {showOnboard && (
        <SectionCard style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            {[
              ['legal_name', 'Legal name', 'e.g. Bake With Tola Ltd'],
              ['display_name', 'Display name', 'Bake With Tola'],
              ['settlement_bank', 'Settlement bank', 'GTBank'],
              ['settlement_account', 'Settlement account', '0123456789'],
              ['webhook_url', 'Webhook URL', 'https://example.com/hooks/gdl'],
              ['brand_color', 'Brand colour', '#FF8C00'],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>{label}</div>
                <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph} data-testid={`merchant-input-${k}`}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 13,
                    border: '1px solid #E5E7EB', borderRadius: 6 }} />
              </div>
            ))}
          </div>
          <button onClick={onboard} data-testid="merchant-submit"
            style={{ marginTop: 12, background: '#B03140', color: '#fff',
              border: 'none', padding: '10px 18px', borderRadius: 8,
              fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Issue API key & go live
          </button>
        </SectionCard>
      )}

      {newKey && (
        <SectionCard style={{ marginBottom: 16, background: '#FEF3C7' }} data-testid="new-merchant-key">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
            One-time API key for {newKey.display_name}
          </div>
          <code style={{ fontSize: 13, fontFamily: 'monospace', color: '#111827',
            background: '#fff', padding: '6px 10px', borderRadius: 6, display: 'inline-block' }}>
            {newKey.api_key}
          </code>
          <div style={{ fontSize: 11, color: '#92400E', marginTop: 6 }}>
            Save this now — it will not be shown again.
          </div>
        </SectionCard>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {merchants.map(m => {
          const ord = orderState[m.id];
          return (
            <SectionCard key={m.id} data-testid={`merchant-row-${m.id}`}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ width: 4, alignSelf: 'stretch', background: m.brand_color, borderRadius: 2 }} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{m.display_name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{m.legal_name}</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                    {m.settlement_bank} · {m.settlement_account}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 110 }}>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>Wallet</div>
                  <div style={{ fontWeight: 700, color: '#10B981' }}>{ngn(m.wallet_balance)}</div>
                  <Badge variant="success">{m.status}</Badge>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
                  {!ord && (
                    <button onClick={() => quickOrder(m)} data-testid={`merchant-create-order-${m.id}`}
                      style={{ padding: '6px 12px', background: '#B03140', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Create ₦25,000 order
                    </button>
                  )}
                  {ord && !ord.settled && (
                    <>
                      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'monospace' }}>
                        VA {ord.virtual_account_number}
                      </div>
                      <button onClick={() => simulatePaid(m)}
                        data-testid={`merchant-simulate-paid-${m.id}`}
                        style={{ padding: '6px 12px', background: '#10B981', color: '#fff',
                          border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Simulate customer paid
                      </button>
                    </>
                  )}
                  {ord && ord.settled && (
                    <div data-testid={`merchant-settled-${m.id}`}
                      style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>
                      ✓ Settled · {ngn(ord.merchant_net)} credited · webhook {ord.webhook_delivered ? '✓ delivered' : 'skipped'}
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>
          );
        })}
        {merchants.length === 0 && (
          <div style={{ color: '#9CA3AF', fontSize: 13, padding: 20, textAlign: 'center' }}>
            No merchants onboarded yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Section E ───────────────────────────────────────────────
function CalculatorPanel() {
  const [servants, setServants] = useState([]);
  const [cs, setCs] = useState(null);
  const [gross, setGross] = useState(500000);
  const [cashPct, setCashPct] = useState(70);
  const [foodPct, setFoodPct] = useState(10);
  const [transportPct, setTransportPct] = useState(10);
  const [calc, setCalc] = useState(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/admin/civil-servants`).then(r => setServants(r.data));
  }, []);

  useEffect(() => {
    if (cs) setGross(cs.monthly_salary);
  }, [cs]);

  const hmoPct = Math.max(100 - cashPct - foodPct - transportPct, 0);

  const preview = async () => {
    setApplied(false);
    try {
      const { data } = await axios.post(`${API}/api/demo/gdl/admin/calculator/preview`, {
        monthly_gross: Number(gross),
        cash_pct: cashPct / 100, food_pct: foodPct / 100,
        transport_pct: transportPct / 100, hmo_pct: hmoPct / 100,
      });
      setCalc(data);
    } catch (e) { alert('Preview failed'); }
  };

  const apply = async () => {
    if (!cs) return;
    try {
      await axios.post(`${API}/api/demo/gdl/admin/calculator/apply/${cs.id}`, {
        monthly_gross: Number(gross),
        cash_pct: cashPct / 100, food_pct: foodPct / 100,
        transport_pct: transportPct / 100, hmo_pct: hmoPct / 100,
      });
      setApplied(true);
    } catch (e) { alert('Apply failed'); }
  };

  return (
    <div data-testid="calculator-panel">
      <SectionCard style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
          Compliant compensation structuring
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.55 }}>
          Restructure a civil servant's total compensation as part cash salary
          plus genuine company-provided benefits (meals, transport, HMO).
          Benefits in kind are tax-exempt under Nigerian law, so the employee
          legitimately takes home more value.
        </div>
      </SectionCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <SectionCard>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#111827' }}>
            1. Select civil servant
          </div>
          <select value={cs?.id || ''} onChange={e => setCs(servants.find(s => s.id === e.target.value))}
            data-testid="calc-cs-select"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13,
              border: '1px solid #E5E7EB', borderRadius: 6 }}>
            <option value="">— Pick one —</option>
            {servants.map(s => (
              <option key={s.id} value={s.id}>{s.full_name} (₦{s.monthly_salary.toLocaleString()})</option>
            ))}
          </select>

          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 16, marginBottom: 10, color: '#111827' }}>
            2. Adjust structure
          </div>
          <Row label="Monthly gross"
            value={<input value={gross} onChange={e => setGross(e.target.value)}
              type="number" data-testid="calc-gross"
              style={{ width: 130, textAlign: 'right', padding: '4px 8px',
                border: '1px solid #E5E7EB', borderRadius: 4, fontSize: 13 }} />} />
          {[
            ['Cash salary %', cashPct, setCashPct, 'calc-cash'],
            ['Food benefit %', foodPct, setFoodPct, 'calc-food'],
            ['Transport benefit %', transportPct, setTransportPct, 'calc-transport'],
          ].map(([l, v, set, tid]) => (
            <Row key={l} label={l} value={
              <input value={v} onChange={e => set(Number(e.target.value))} type="number"
                min={0} max={100} data-testid={tid}
                style={{ width: 90, textAlign: 'right', padding: '4px 8px',
                  border: '1px solid #E5E7EB', borderRadius: 4, fontSize: 13 }} />
            } />
          ))}
          <Row label="HMO benefit %" value={<span style={{ fontWeight: 600, color: '#B03140' }}>{hmoPct}%</span>} />

          <button onClick={preview} data-testid="calc-preview-btn"
            style={{ marginTop: 12, width: '100%', background: '#B03140', color: '#fff',
              border: 'none', padding: '10px', borderRadius: 8,
              fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Calculate net pay before vs after
          </button>
        </SectionCard>

        {calc && (
          <SectionCard data-testid="calc-result">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div style={{ padding: 12, background: '#F3F4F6', borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>
                  Before structuring
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>PAYE</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#EF4444' }}>
                  {ngn(calc.before_structuring.paye_monthly)}
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>Net take-home</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  {ngn(calc.before_structuring.net_take_home)}
                </div>
              </div>
              <div style={{ padding: 12, background: '#ECFDF5', borderRadius: 8,
                            border: '1px solid #10B981' }}>
                <div style={{ fontSize: 11, color: '#065F46', textTransform: 'uppercase', fontWeight: 700 }}>
                  After structuring
                </div>
                <div style={{ fontSize: 11, color: '#065F46', marginTop: 8 }}>PAYE on cash portion</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10B981' }}>
                  {ngn(calc.after_structuring.paye_monthly)}
                </div>
                <div style={{ fontSize: 11, color: '#065F46', marginTop: 6 }}>Total take-home value</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>
                  {ngn(calc.after_structuring.total_take_home_value)}
                </div>
              </div>
            </div>
            <div style={{ padding: 10, background: '#FEF3C7', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                Monthly improvement (compliant value delivered)
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#10B981', marginTop: 2 }}>
                +{ngn(calc.monthly_improvement)}
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 10, lineHeight: 1.55 }}>
              {calc.compliance_note}
            </div>
            {cs && (
              <button onClick={apply} data-testid="calc-apply-btn"
                disabled={applied}
                style={{ marginTop: 12, width: '100%', background: applied ? '#9CA3AF' : '#10B981',
                  color: '#fff', border: 'none', padding: '10px', borderRadius: 8,
                  fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {applied ? `✓ Applied to ${cs.full_name}` : `Apply structure to ${cs.full_name}`}
              </button>
            )}
          </SectionCard>
        )}
      </div>
    </div>
  );
}

// ─── Section F ───────────────────────────────────────────────
function GdlPayPanel() {
  const [employers, setEmployers] = useState([]);
  const [emp, setEmp] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/admin/employers`).then(r => {
      setEmployers(r.data);
      if (r.data[0]) setEmp(r.data[0]);
    });
  }, []);
  useEffect(() => {
    if (!emp) return;
    axios.get(`${API}/api/demo/gdl/admin/employers/${emp.id}/employees`).then(r => setEmployees(r.data));
    axios.get(`${API}/api/demo/gdl/admin/employers/${emp.id}/payroll-runs`).then(r => setRuns(r.data));
  }, [emp, lastRun]);

  const runPayroll = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/api/demo/gdl/admin/employers/${emp.id}/payroll-run`, {});
      setLastRun(data);
    } catch (e) { alert(e.response?.data?.detail || 'Payroll failed'); }
    setRunning(false);
  };

  if (!emp) return <div style={{ color: '#6B7280' }}>Loading…</div>;

  return (
    <div data-testid="gdlpay-panel">
      <SectionCard style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          GDL Pay — same engine, corporate audience
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.55 }}>
          The same Pebbles wallet + ledger + benefits engine that powers the
          civil-servant flow, configured for employers. A separate revenue
          line from civil-servant lending.
        </div>
      </SectionCard>

      <SectionCard style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{emp.legal_name}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              {emp.industry} · {emp.headcount} employees · Wallet {ngn(emp.wallet_balance)}
            </div>
          </div>
          <button onClick={runPayroll} disabled={running}
            data-testid="run-payroll-btn"
            style={{ background: '#B03140', color: '#fff', border: 'none',
              padding: '10px 18px', borderRadius: 8, fontWeight: 600,
              fontSize: 13, cursor: 'pointer' }}>
            {running ? 'Running payroll…' : 'Run full payroll (1-click)'}
          </button>
        </div>
      </SectionCard>

      {lastRun && (
        <SectionCard style={{ marginBottom: 16, background: 'linear-gradient(135deg, #ECFDF5, #fff)' }}
          data-testid="payroll-result">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 8 }}>
            ✓ {lastRun.cycle_label} executed
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {[
              ['Cash salary', lastRun.total_cash],
              ['Benefits delivered', lastRun.total_benefits],
              ['PAYE remitted', lastRun.total_tax_paye],
            ].map(([l, v]) => (
              <div key={l}>
                <div style={{ fontSize: 11, color: '#6B7280' }}>{l}</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{ngn(v)}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        Employees ({employees.length})
      </div>
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Name', 'Role', 'Gross', 'Cash', 'Food', 'Transport', 'HMO'].map(h => (
                <th key={h} style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#6B7280' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map(e => (
              <tr key={e.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{e.full_name}</td>
                <td style={{ padding: '8px 10px', color: '#6B7280' }}>{e.role}</td>
                <td style={{ padding: '8px 10px' }}>{ngn(e.monthly_gross)}</td>
                <td style={{ padding: '8px 10px' }}>{ngn(e.cash_salary)}</td>
                <td style={{ padding: '8px 10px' }}>{ngn(e.benefit_food)}</td>
                <td style={{ padding: '8px 10px' }}>{ngn(e.benefit_transport)}</td>
                <td style={{ padding: '8px 10px' }}>{ngn(e.benefit_hmo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {runs.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginTop: 16, marginBottom: 8 }}>
            Run history
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {runs.map(r => (
              <SectionCard key={r.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.cycle_label}</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                      {r.employee_count} employees · executed {new Date(r.executed_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    <div>Cash {ngn(r.total_cash)}</div>
                    <div>Benefits {ngn(r.total_benefits)}</div>
                  </div>
                </div>
              </SectionCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: 13, padding: '4px 0' }}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      {value}
    </div>
  );
}
